import { Navigate } from 'react-router-dom';
import type { ReactElement, ReactNode } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import {
  Activity,
  BarChart3,
  Blocks,
  Brain,
  Building2,
  Briefcase,
  Calendar,
  ClipboardList,
  Columns,
  Cpu,
  FileStack,
  FileText,
  GitBranch,
  Home,
  LayoutDashboard,
  Lightbulb,
  ListChecks,
  Mail,
  Megaphone,
  MessageSquare,
  Mic,
  Palette,
  Search,
  Send,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  NotebookPen,
  TextCursorInput,
  UserCircle,
  UserCog,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { WorkspaceHome } from './WorkspaceHome';
import { HomeV2 } from '@/modules/dashboard/variants/HomeV2';
import { HomeV3 } from '@/modules/dashboard/variants/HomeV3';
import { HomeV4 } from '@/modules/dashboard/variants/HomeV4';
import { HomeV5 } from '@/modules/dashboard/variants/HomeV5';
import { HomeV31 } from '@/modules/dashboard/variants/HomeV31';
import { HomeV32 } from '@/modules/dashboard/variants/HomeV32';
import { HomeV33 } from '@/modules/dashboard/variants/HomeV33';
import { HomeV34 } from '@/modules/dashboard/variants/HomeV34';
import { PermissionMatrixPage } from './PermissionMatrixPage';
import { MigrationPage } from './MigrationPage';
import { NotetakersPage } from './NotetakersPage';
import { NewTabPage } from './NewTabPage';
import { JobCardLabPage } from './JobCardLabPage';
import { PipelineLabPage } from './PipelineLabPage';
import { SearchPage } from './search/SearchPage';
import TalentPoolSearchPage from './talent-pool/TalentPoolSearchPage';
import { hasCryptoLocalSearch } from '@/lib/orgGates';
import { SearchV3ListPage } from './search-v3/SearchV3ListPage';
import { SearchV3AutoSourceOpsPage } from './staff/SearchV3AutoSourceOpsPage';
import { ObservabilityPage } from './staff/ObservabilityPage';
import { TasksPage } from './TasksPage';
import { ChatsListPage } from './ChatsListPage';
import { AgentChatsListPage } from './AgentChatsListPage';
import { AgentNewChatPage } from './AgentNewChatPage';
import { SessionsListPage } from './SessionsListPage';
import { JobsListPage } from './JobsListPage';
import { OutreachInboxPage } from './OutreachInboxPage';
import { v2RouteElements } from './v2-route-adapters';
import { BrainLayout } from './brain/BrainLayout';
import { BlueprintTypePage } from './brain/BlueprintTypePage';
import { KnowledgeAppliesPage } from './brain/KnowledgeAppliesPage';
import { JacintaMemoryPage } from './brain/JacintaMemoryPage';
import { SkillsPage } from './brain/SkillsPage';
import { OrgInfoPage } from './brain/OrgInfoPage';
import { HiringPhilosophyPage } from './brain/HiringPhilosophyPage';
import { HeadcountPlanPage } from './brain/HeadcountPlanPage';
import { EmploymentHistoryPage } from './brain/EmploymentHistoryPage';
import { SettingsLayout } from './settings/SettingsLayout';
import { GeneralPage } from './settings/GeneralPage';
import { AccountPage } from './settings/AccountPage';
import { OrganizationPage } from './settings/OrganizationPage';
import { JobBoardPage } from './settings/JobBoardPage';
import { IntegrationsPage } from './settings/IntegrationsPage';
import { AtsPage } from './settings/AtsPage';
import { CalendarSettingsPage } from './settings/CalendarSettingsPage';
import { EmailPage } from './settings/EmailPage';
import { OutreachAccountsPage } from './settings/OutreachAccountsPage';

export type RouteStatus = 'frame-ready' | 'migration-target' | 'ported';

export type AppRoute = {
  id: string;
  path: string;
  title: string;
  subtitle?: string;
  sourcePath?: string;
  legacyPath?: string;
  status: RouteStatus;
  icon: LucideIcon;
  element: ReactElement;
  requireAnyPermission?: string[];
  tabLabel?: string;
  /**
   * Whether this route's instance is kept mounted (with React's <Activity>) when its
   * tab is hidden. Default `true` — the whole point of multi-tab keep-alive.
   * Set `false` for routes whose singletons or background work haven't been audited
   * for parallel-mount safety; they fall back to today's remount-on-activate behavior.
   */
  keepAlive?: boolean;
};

type RouteMeta = Omit<AppRoute, 'element'> & {
  kind?:
    | 'home'
    | 'new-tab'
    | 'permission'
    | 'migration'
    | 'notetakers'
    | 'search'
    | 'redirect'
    | 'settings-redirect'
    | 'settings-general'
    | 'settings-account'
    | 'settings-organization'
    | 'settings-job-board'
    | 'settings-integrations'
    | 'settings-ats'
    | 'settings-calendar'
    | 'settings-email'
    | 'settings-outreach-accounts'
    | 'brain-redirect'
    | 'brain-blueprints-redirect'
    | 'brain-blueprints-type'
    | 'brain-knowledge-redirect'
    | 'brain-knowledge-applies'
    | 'brain-memory'
    | 'brain-skills'
    | 'brain-company-redirect'
    | 'brain-company-profile'
    | 'brain-company-philosophy'
    | 'brain-company-headcount'
    | 'brain-company-employment-history'
    | 'tasks'
    | 'sessions-list'
    | 'jobs-summary'
    | 'outreach-inbox'
    | 'chats-list'
    | 'agent-chats-list'
    | 'agent-new-chat'
    | 'search-v2-list'
    | 'search-v2-new'
    | 'search-v3-list'
    | 'search-v3-new'
    | 'staff-search-v3-autosource'
    | 'staff-observability'
    | 'talent-pool'
    | 'home-v2'
    | 'home-v3'
    | 'home-v4'
    | 'home-v5'
    | 'home-v3-1'
    | 'home-v3-2'
    | 'home-v3-3'
    | 'home-v3-4'
    | 'lab-job-cards'
    | 'lab-pipeline';
};

const REDIRECT_MAP: Record<string, string> = {
  'members-redirect': '/settings/organization?tab=members',
  'invite-redirect': '/settings/organization?tab=members&invite=1',
  'roles-redirect': '/settings/organization?tab=roles',
  'integrations-redirect': '/settings/integrations',
  'integration-accounts-redirect': '/settings/integrations',
  // Outreach settings moved out of the fullscreen /v2/settings shell into the
  // Outreach hub so the shared tab strip is consistent across all four surfaces.
  'settings-outreach': '/v2/outreach/settings',
};

const routeMeta: RouteMeta[] = [
  {
    id: 'home',
    path: '/',
    title: 'Workspace Home',
    tabLabel: 'Home',
    subtitle: 'Current organization, workspace, and migration status.',
    status: 'frame-ready',
    icon: Home,
    kind: 'home',
  },
  {
    id: 'home-v2',
    path: '/home-v2',
    title: 'Home — v2 (Workspace warm)',
    tabLabel: 'Home v2',
    subtitle: 'Sans-only, warm three-tone, candy data, hairlines.',
    status: 'frame-ready',
    icon: Home,
    kind: 'home-v2',
  },
  {
    id: 'home-v3',
    path: '/home-v3',
    title: 'Home — v3 (Bento asymmetric)',
    tabLabel: 'Home v3',
    subtitle: 'Two-column bento, briefing-first, today rail.',
    status: 'frame-ready',
    icon: Home,
    kind: 'home-v3',
  },
  {
    id: 'home-v4',
    path: '/home-v4',
    title: 'Home — v4 (Bright candy)',
    tabLabel: 'Home v4',
    subtitle: 'Saturated tile colors, big sans headlines.',
    status: 'frame-ready',
    icon: Home,
    kind: 'home-v4',
  },
  {
    id: 'home-v5',
    path: '/home-v5',
    title: 'Home — v5 (Editorial elegant)',
    tabLabel: 'Home v5',
    subtitle: 'Pure cream, 720px reading column, briefing-as-page.',
    status: 'frame-ready',
    icon: Home,
    kind: 'home-v5',
  },
  {
    id: 'home-v3-1',
    path: '/home-v3-1',
    title: 'Home — v3.1 (Paper canvas)',
    tabLabel: 'v3.1',
    subtitle: 'Off-white page; cream hero pops; goal progress + mini calendar.',
    status: 'frame-ready',
    icon: Home,
    kind: 'home-v3-1',
  },
  {
    id: 'home-v3-2',
    path: '/home-v3-2',
    title: 'Home — v3.2 (Dark-hero magazine)',
    tabLabel: 'v3.2',
    subtitle: 'Deep ink hero; live status bar; ready-for-you grid.',
    status: 'frame-ready',
    icon: Home,
    kind: 'home-v3-2',
  },
  {
    id: 'home-v3-3',
    path: '/home-v3-3',
    title: 'Home — v3.3 (Floating monument)',
    tabLabel: 'v3.3',
    subtitle: 'Big hero, accent ring; animated funnel + workload meter.',
    status: 'frame-ready',
    icon: Home,
    kind: 'home-v3-3',
  },
  {
    id: 'home-v3-4',
    path: '/home-v3-4',
    title: 'Home — v3.4 (Editorial bands)',
    tabLabel: 'v3.4',
    subtitle: 'Dark section bands; Jacinta-edited digest; chapter focus.',
    status: 'frame-ready',
    icon: Home,
    kind: 'home-v3-4',
  },
  {
    id: 'new-tab',
    path: '/new-tab',
    title: 'New Tab',
    subtitle: 'Suggested ATS destinations.',
    status: 'frame-ready',
    icon: Home,
    kind: 'new-tab',
  },
  {
    id: 'agent-dashboard',
    path: '/v2/agent-dashboard',
    title: 'Agent Dashboard',
    subtitle: 'AI agent activity and hiring operations.',
    sourcePath: 'apps/ats/src/app/v2/agent-dashboard/page.tsx',
    legacyPath: '/v2/agent-dashboard',
    status: 'migration-target',
    icon: Activity,
  },
  {
    id: 'notetakers',
    path: '/notetakers',
    title: 'Notetakers',
    tabLabel: 'Notetakers',
    subtitle: 'Auto-record and transcribe interviews with Recall.ai.',
    sourcePath: 'apps/ats-spa/src/pages/NotetakersPage.tsx',
    status: 'frame-ready',
    icon: NotebookPen,
    kind: 'notetakers',
  },
  {
    id: 'search',
    path: '/search',
    title: 'Search',
    tabLabel: 'Search',
    subtitle: 'Recruiter search across candidate profiles with AI-evaluated criteria.',
    sourcePath: 'apps/ats-spa/src/pages/search/SearchPage.tsx',
    status: 'frame-ready',
    icon: Search,
    kind: 'search',
    requireAnyPermission: ['search.view'],
  },
  {
    id: 'jobs',
    path: '/v2/jobs',
    title: 'Job roles',
    tabLabel: 'Job roles',
    subtitle: 'Role list and hiring pipeline entry point.',
    sourcePath: 'apps/ats/src/app/v2/jobs/page.tsx',
    legacyPath: '/v2/jobs',
    status: 'migration-target',
    icon: Briefcase,
    requireAnyPermission: ['job.view'],
  },
  {
    // Default-org roster: fast job list + ONE batch roster-summary call that
    // fills every card's sourcing/outreach line. Separate from the v2 `jobs`
    // migration target above.
    id: 'jobs-summary',
    path: '/jobs',
    title: 'Jobs',
    tabLabel: 'Jobs',
    subtitle: 'Your open roles, with live sourcing and outreach at a glance.',
    sourcePath: 'apps/ats-spa/src/pages/JobsListPage.tsx',
    status: 'frame-ready',
    icon: Briefcase,
    kind: 'jobs-summary',
    requireAnyPermission: ['job.view'],
  },
  {
    // Default-org conversations list — the user's 'general' agent sessions.
    id: 'sessions-list',
    path: '/sessions',
    title: 'Sessions',
    tabLabel: 'Sessions',
    subtitle: 'Every conversation you have started with Lantern.',
    sourcePath: 'apps/ats-spa/src/pages/SessionsListPage.tsx',
    status: 'frame-ready',
    icon: MessageSquare,
    kind: 'sessions-list',
  },
  {
    // Default-org outreach inbox — every campaign reply in one place, WITHOUT the
    // full outreach-hub chrome. Lives under the minimal nav (New session /
    // Sessions / Jobs / Inbox). Distinct from the preset-org /v2/inbox hub page.
    id: 'outreach-inbox',
    path: '/inbox',
    title: 'Inbox',
    tabLabel: 'Inbox',
    subtitle: 'Replies from every campaign, in one place.',
    sourcePath: 'apps/ats-spa/src/pages/OutreachInboxPage.tsx',
    status: 'frame-ready',
    icon: Mail,
    kind: 'outreach-inbox',
    requireAnyPermission: ['outreach.view'],
  },
  {
    id: 'jobs-v2',
    path: '/v2/jobs-v2',
    title: 'Jobs v2',
    subtitle: 'Alternate jobs workspace from the current v2 app.',
    sourcePath: 'apps/ats/src/app/v2/jobs-v2/page.tsx',
    legacyPath: '/v2/jobs-v2',
    status: 'migration-target',
    icon: LayoutDashboard,
  },
  {
    id: 'job-detail',
    path: '/v2/jobs/:id',
    title: 'Job Detail',
    tabLabel: 'Job',
    subtitle: 'Job overview, pipeline, and candidate entry points.',
    sourcePath: 'apps/ats/src/app/v2/jobs/[id]/page.tsx',
    legacyPath: '/v2/jobs/:id',
    status: 'migration-target',
    icon: Briefcase,
    requireAnyPermission: ['job.view'],
  },
  {
    id: 'new-job',
    path: '/v2/jobs/new',
    title: 'New Job',
    subtitle: 'Create a new role.',
    sourcePath: 'apps/ats/src/app/v2/jobs/new/page.tsx',
    legacyPath: '/v2/jobs/new',
    status: 'migration-target',
    icon: ListChecks,
  },
  {
    // Phase 1 (agent-worker extensibility) — single-turn job creation:
    // describe the role in free text (+ attachments), Lantern's
    // job_create agent drafts the job and navigates to it.
    id: 'new-job-agentic',
    path: '/v2/jobs/new/agentic',
    title: 'New Job with Lantern',
    subtitle: 'Describe the role — Lantern drafts the job.',
    sourcePath: 'apps/ats-spa/src/app/v2/jobs/new/agentic/page.tsx',
    legacyPath: '/v2/jobs/new/agentic',
    status: 'migration-target',
    icon: Sparkles,
    requireAnyPermission: ['job.create'],
  },
  {
    id: 'people',
    path: '/v2/people',
    title: 'People',
    subtitle: 'Candidates and contacts.',
    sourcePath: 'apps/ats/src/app/v2/people/page.tsx',
    legacyPath: '/v2/people',
    status: 'migration-target',
    icon: Users,
    requireAnyPermission: ['person.view'],
  },
  {
    id: 'custom-fields',
    path: '/v2/people/custom-fields',
    title: 'Custom Fields',
    subtitle: 'People data model controls.',
    sourcePath: 'apps/ats/src/app/v2/people/custom-fields/page.tsx',
    legacyPath: '/v2/people/custom-fields',
    status: 'migration-target',
    icon: TextCursorInput,
    requireAnyPermission: ['person.manage_custom_fields', 'organization.edit'],
  },
  {
    id: 'cv-screening-create',
    path: '/v2/cv-screening/create',
    title: 'Create CV Screening',
    subtitle: 'Set up a CV screening workflow.',
    sourcePath: 'apps/ats/src/app/v2/cv-screening/create/page.tsx',
    legacyPath: '/v2/cv-screening/create',
    status: 'migration-target',
    icon: FileText,
  },
  {
    id: 'cv-screening-edit',
    path: '/v2/cv-screening/:id/edit',
    title: 'Edit CV Screening',
    tabLabel: 'CV Screening',
    subtitle: 'Tune CV screening criteria and verdict rules.',
    sourcePath: 'apps/ats/src/app/v2/cv-screening/[id]/edit/page.tsx',
    legacyPath: '/v2/cv-screening/:id/edit',
    status: 'migration-target',
    icon: FileText,
  },
  {
    id: 'ai-interview-create',
    path: '/v2/ai-interview/create',
    title: 'Create AI Interview',
    subtitle: 'Set up an AI interview workflow.',
    sourcePath: 'apps/ats/src/app/v2/ai-interview/create/page.tsx',
    legacyPath: '/v2/ai-interview/create',
    status: 'migration-target',
    icon: Mic,
  },
  {
    id: 'ai-interview-edit',
    path: '/v2/ai-interview/:id/edit',
    title: 'Edit AI Interview',
    tabLabel: 'AI Interview',
    subtitle: 'Tune AI interview questions, language, and media.',
    sourcePath: 'apps/ats/src/app/v2/ai-interview/[id]/edit/page.tsx',
    legacyPath: '/v2/ai-interview/:id/edit',
    status: 'migration-target',
    icon: Mic,
  },
  {
    id: 'job-edit-root',
    path: '/v2/jobs/:id/edit',
    title: 'Edit Job',
    tabLabel: 'Edit Job',
    subtitle: 'Edit profile, evaluation, pipeline, and automation.',
    sourcePath: 'apps/ats/src/app/v2/jobs/[id]/edit/page.tsx',
    legacyPath: '/v2/jobs/:id/edit',
    status: 'migration-target',
    icon: SlidersHorizontal,
  },
  {
    id: 'job-edit-subpage',
    path: '/v2/jobs/:id/edit/:subpage',
    title: 'Edit Job',
    tabLabel: 'Edit Job',
    subtitle: 'Job setup wizard section.',
    sourcePath: 'apps/ats/src/app/v2/jobs/[id]/edit/[subpage]/page.tsx',
    legacyPath: '/v2/jobs/:id/edit/:subpage',
    status: 'migration-target',
    icon: SlidersHorizontal,
  },
  // NOTE: /v2/jobs/:id/edit-final (full-page job launch + edit) and
  // /v2/jobs/:id/manage (agentic job workspace) are intentionally NOT in this
  // registry — they mount outside AppShell in App.tsx (no sidebar/tabs).
  {
    id: 'job-icp-builder',
    path: '/v2/jobs/:id/icp-builder',
    title: 'ICP Builder',
    tabLabel: 'ICP Builder',
    subtitle: 'Interactive ideal candidate profile builder.',
    sourcePath: 'apps/ats/src/app/v2/jobs/[id]/icp-builder/page.tsx',
    legacyPath: '/v2/jobs/:id/icp-builder',
    status: 'migration-target',
    icon: Cpu,
  },
  {
    id: 'job-outreach',
    path: '/v2/jobs/:id/outreach',
    title: 'Outreach',
    tabLabel: 'Outreach',
    subtitle: 'Spin up an outreach campaign for this role.',
    sourcePath: 'apps/ats-spa/src/app/v2/jobs/[id]/outreach/page.tsx',
    legacyPath: '/v2/jobs/:id/outreach',
    status: 'ported',
    icon: Send,
    requireAnyPermission: ['outreach.view'],
  },
  {
    id: 'job-campaign-sequence',
    path: '/v2/jobs/:id/campaigns/:campaignId/sequence',
    title: 'Sequence',
    tabLabel: 'Sequence',
    subtitle: 'Edit the outreach branch tree for this campaign.',
    sourcePath: 'apps/ats-spa/src/app/v2/jobs/[id]/campaigns/[campaignId]/sequence/page.tsx',
    legacyPath: '/v2/jobs/:id/campaigns/:campaignId/sequence',
    status: 'ported',
    icon: GitBranch,
    requireAnyPermission: ['outreach.edit'],
  },
  {
    id: 'outreach-campaigns',
    path: '/v2/outreach/campaigns',
    title: 'Campaigns',
    tabLabel: 'Campaigns',
    subtitle: 'Stand-alone outreach campaigns — upload a list, draft, and launch.',
    sourcePath: 'apps/ats-spa/src/app/v2/outreach/campaigns/page.tsx',
    status: 'ported',
    icon: Megaphone,
    requireAnyPermission: ['outreach.view'],
  },
  {
    id: 'campaign-detail',
    path: '/v2/outreach/campaigns/:campaignId',
    title: 'Campaign',
    tabLabel: 'Campaign',
    subtitle: 'Guided campaign builder — audience, sequence, mailbox, launch.',
    sourcePath: 'apps/ats-spa/src/app/v2/outreach/campaigns/[campaignId]/page.tsx',
    status: 'ported',
    icon: Megaphone,
    requireAnyPermission: ['outreach.view'],
  },
  {
    id: 'campaign-sequence',
    path: '/v2/outreach/campaigns/:campaignId/sequence',
    title: 'Sequence',
    tabLabel: 'Sequence',
    subtitle: 'Edit the outreach branch tree for this campaign.',
    sourcePath: 'apps/ats-spa/src/app/v2/outreach/campaigns/[campaignId]/sequence/page.tsx',
    status: 'ported',
    icon: GitBranch,
    requireAnyPermission: ['outreach.edit'],
  },
  {
    id: 'inbox',
    path: '/v2/inbox',
    title: 'Inbox',
    tabLabel: 'Inbox',
    subtitle: 'Cross-job CRM inbox — every campaign reply in one place.',
    sourcePath: 'apps/ats-spa/src/app/v2/inbox/page.tsx',
    legacyPath: '/v2/inbox',
    status: 'ported',
    icon: Mail,
    requireAnyPermission: ['outreach.view'],
  },
  {
    id: 'outreach-analytics',
    path: '/v2/outreach/analytics',
    title: 'Analytics',
    tabLabel: 'Analytics',
    subtitle: 'Outreach performance across every role — reply rates, channels, best-responding segments.',
    sourcePath: 'apps/ats-spa/src/app/v2/outreach/analytics/page.tsx',
    legacyPath: '/v2/outreach/analytics',
    status: 'ported',
    icon: BarChart3,
    requireAnyPermission: ['outreach.view'],
  },
  {
    // Canonical home for outreach settings — lives in the hub shell (with the
    // sidebar + shared Outreach tab strip), NOT the fullscreen /v2/settings frame.
    id: 'outreach-settings',
    path: '/v2/outreach/settings',
    title: 'Outreach Settings',
    tabLabel: 'Outreach Settings',
    subtitle: 'Accounts, safety caps, signatures, and suppression.',
    sourcePath: 'apps/ats-spa/src/app/v2/settings/outreach/page.tsx',
    legacyPath: '/v2/settings/outreach',
    status: 'ported',
    icon: SlidersHorizontal,
    requireAnyPermission: ['outreach.view'],
  },
  {
    // Back-compat: the old fullscreen path redirects into the hub (REDIRECT_MAP).
    id: 'settings-outreach',
    path: '/v2/settings/outreach',
    title: 'Outreach Settings',
    subtitle: 'Redirects to the Outreach hub settings.',
    status: 'ported',
    icon: SlidersHorizontal,
    kind: 'redirect',
  },
  {
    id: 'job-plan',
    path: '/v2/jobs/:id/plan',
    title: 'Plan Mode',
    tabLabel: 'Plan',
    subtitle: 'Hiring plan review and publish flow.',
    sourcePath: 'apps/ats/src/app/v2/jobs/[id]/plan/page.tsx',
    legacyPath: '/v2/jobs/:id/plan',
    status: 'migration-target',
    icon: ListChecks,
  },
  {
    id: 'job-quick-setup',
    path: '/v2/jobs/:id/quick-setup',
    title: 'Quick Setup',
    subtitle: 'Generate a plan from an existing job.',
    sourcePath: 'apps/ats/src/app/v2/jobs/[id]/quick-setup/page.tsx',
    legacyPath: '/v2/jobs/:id/quick-setup',
    status: 'migration-target',
    icon: Cpu,
  },
  {
    id: 'new-role-quick-setup',
    path: '/v2/jobs/new-role/quick-setup',
    title: 'New Role Quick Setup',
    tabLabel: 'Quick Setup',
    subtitle: 'Generate a new role and plan.',
    sourcePath: 'apps/ats/src/app/v2/jobs/new-role/quick-setup/page.tsx',
    legacyPath: '/v2/jobs/new-role/quick-setup',
    status: 'migration-target',
    icon: Cpu,
  },
  {
    id: 'new-role-icp-builder',
    path: '/v2/jobs/new-role/icp-builder',
    title: 'New Role ICP Builder',
    tabLabel: 'ICP Builder',
    subtitle: 'Generate a new role through the ICP builder.',
    sourcePath: 'apps/ats/src/app/v2/jobs/new-role/icp-builder/page.tsx',
    legacyPath: '/v2/jobs/new-role/icp-builder',
    status: 'migration-target',
    icon: Cpu,
  },
  {
    id: 'new-role-agentic',
    path: '/v2/jobs/new-role/agentic',
    title: 'Agentic Setup',
    tabLabel: 'Agentic',
    subtitle: 'Slack-to-launch role setup — chat with Lantern, see live artifacts.',
    sourcePath: 'apps/ats-spa/src/app/v2/jobs/new-role/agentic/page.tsx',
    status: 'frame-ready',
    icon: Sparkles,
  },
  {
    id: 'members-redirect',
    path: '/v2/settings/members',
    title: 'Members',
    subtitle: 'Redirects to consolidated organization settings.',
    status: 'ported',
    icon: UserCog,
    kind: 'redirect',
  },
  {
    id: 'invite-redirect',
    path: '/v2/settings/invite',
    title: 'Invite',
    subtitle: 'Redirects to consolidated organization settings.',
    status: 'ported',
    icon: UserPlus,
    kind: 'redirect',
  },
  {
    id: 'roles-redirect',
    path: '/v2/settings/roles',
    title: 'Roles',
    subtitle: 'Redirects to consolidated organization settings.',
    status: 'ported',
    icon: Shield,
    kind: 'redirect',
  },
  {
    id: 'integrations-redirect',
    path: '/v2/settings/integrations',
    title: 'Integrations',
    subtitle: 'Redirects to Integrations settings.',
    status: 'ported',
    icon: Blocks,
    kind: 'redirect',
  },
  {
    id: 'integration-accounts-redirect',
    path: '/v2/settings/integrations/accounts',
    title: 'Integration Accounts',
    subtitle: 'Redirects to Integrations settings.',
    status: 'ported',
    icon: Blocks,
    kind: 'redirect',
  },
  {
    id: 'calendar',
    path: '/v2/calendar',
    title: 'Calendar',
    tabLabel: 'Calendar',
    subtitle: 'Scheduling, booking pages, and availability.',
    sourcePath: 'apps/ats-spa/src/app/v2/calendar/page.tsx',
    legacyPath: '/bookings',
    status: 'ported',
    icon: Calendar,
  },
  {
    id: 'permission-test',
    path: '/v2/permission-test',
    title: 'Permission Test',
    subtitle: 'Live permission matrix from the v2 auth session.',
    sourcePath: 'apps/ats/src/app/v2/permission-test/page.tsx',
    legacyPath: '/v2/permission-test',
    status: 'ported',
    icon: Cpu,
    kind: 'permission',
  },
  {
    id: 'design-system',
    path: '/v2/design-system',
    title: 'Design System',
    subtitle: 'V2 design stories and component checks.',
    sourcePath: 'apps/ats/src/app/v2/design-system/page.tsx',
    legacyPath: '/v2/design-system',
    status: 'migration-target',
    icon: Palette,
  },
  {
    id: 'playground-kanban',
    path: '/v2/playground/kanban',
    title: 'Kanban Playground',
    sourcePath: 'apps/ats/src/app/v2/playground/kanban/page.tsx',
    legacyPath: '/v2/playground/kanban',
    status: 'migration-target',
    icon: Columns,
  },
  {
    id: 'playground-kanban-v2',
    path: '/v2/playground/kanban-v2',
    title: 'Kanban v2',
    sourcePath: 'apps/ats/src/app/v2/playground/kanban-v2/page.tsx',
    legacyPath: '/v2/playground/kanban-v2',
    status: 'migration-target',
    icon: Columns,
  },
  {
    id: 'playground-kanban-v3',
    path: '/v2/playground/kanban-v3',
    title: 'Kanban v3',
    sourcePath: 'apps/ats/src/app/v2/playground/kanban-v3/page.tsx',
    legacyPath: '/v2/playground/kanban-v3',
    status: 'migration-target',
    icon: Columns,
  },
  {
    id: 'playground-report',
    path: '/v2/playground/candidate-report',
    title: 'Candidate Report',
    sourcePath: 'apps/ats/src/app/v2/playground/candidate-report/page.tsx',
    legacyPath: '/v2/playground/candidate-report',
    status: 'migration-target',
    icon: FileText,
  },
  {
    id: 'playground-report-v2',
    path: '/v2/playground/report-v2',
    title: 'Report v2',
    sourcePath: 'apps/ats/src/app/v2/playground/report-v2/page.tsx',
    legacyPath: '/v2/playground/report-v2',
    status: 'migration-target',
    icon: FileText,
  },
  {
    id: 'playground-report-v2-detail',
    path: '/v2/playground/report-v2/:id',
    title: 'Report v2 Detail',
    tabLabel: 'Report v2',
    sourcePath: 'apps/ats/src/app/v2/playground/report-v2/[id]/page.tsx',
    legacyPath: '/v2/playground/report-v2/:id',
    status: 'migration-target',
    icon: FileText,
  },
  {
    id: 'playground-report-v3',
    path: '/v2/playground/report-v3',
    title: 'Report v3',
    sourcePath: 'apps/ats/src/app/v2/playground/report-v3/page.tsx',
    legacyPath: '/v2/playground/report-v3',
    status: 'migration-target',
    icon: FileText,
  },
  {
    id: 'playground-report-v3-detail',
    path: '/v2/playground/report-v3/:id',
    title: 'Report v3 Detail',
    tabLabel: 'Report v3',
    sourcePath: 'apps/ats/src/app/v2/playground/report-v3/[id]/page.tsx',
    legacyPath: '/v2/playground/report-v3/:id',
    status: 'migration-target',
    icon: FileText,
  },
  {
    id: 'pipeline',
    path: '/pipeline',
    title: 'Pipeline Designer',
    subtitle: 'Pipeline stages and flow editor.',
    sourcePath: 'apps/ats/src/modules/pipeline/components/PipelineDesigner.tsx',
    legacyPath: '/pipeline',
    status: 'migration-target',
    icon: GitBranch,
  },
  {
    id: 'settings',
    path: '/settings',
    title: 'Settings',
    subtitle: 'Workspace, organization, and integrations.',
    status: 'ported',
    icon: Settings,
    kind: 'settings-redirect',
  },
  {
    id: 'settings-general',
    path: '/settings/general',
    title: 'General Settings',
    status: 'ported',
    icon: Settings,
    kind: 'settings-general',
    requireAnyPermission: ['organization.edit'],
  },
  {
    id: 'settings-account',
    path: '/settings/account',
    title: 'Account Settings',
    status: 'ported',
    icon: UserCircle,
    kind: 'settings-account',
  },
  {
    id: 'organization-settings',
    path: '/settings/organization',
    title: 'Organization Settings',
    status: 'ported',
    icon: Building2,
    kind: 'settings-organization',
    requireAnyPermission: ['organization.edit', 'workspace.manage_members', 'workspace.manage_roles'],
  },
  {
    id: 'settings-job-board',
    path: '/settings/job-board',
    title: 'Job Board Settings',
    status: 'ported',
    icon: Briefcase,
    kind: 'settings-job-board',
  },
  {
    id: 'settings-integrations',
    path: '/settings/integrations',
    title: 'Integrations Settings',
    status: 'ported',
    icon: Blocks,
    kind: 'settings-integrations',
  },
  {
    id: 'settings-ats',
    path: '/settings/ats',
    title: 'ATS Settings',
    status: 'ported',
    icon: ClipboardList,
    kind: 'settings-ats',
  },
  {
    id: 'settings-calendar',
    path: '/settings/calendar',
    title: 'Calendar Settings',
    status: 'ported',
    icon: Calendar,
    kind: 'settings-calendar',
  },
  {
    id: 'settings-email',
    path: '/settings/email',
    title: 'Email Settings',
    status: 'ported',
    icon: Mail,
    kind: 'settings-email',
  },
  {
    id: 'settings-outreach-accounts',
    path: '/settings/outreach-accounts',
    title: 'Outreach Accounts',
    status: 'ported',
    icon: Send,
    kind: 'settings-outreach-accounts',
  },
  {
    // Brain has personal-scope Knowledge (gateless), so the entry route is
    // open to any authenticated user — page-level perms gate the rest.
    id: 'brain',
    path: '/brain',
    title: 'Brain',
    subtitle: 'Reusable templates and agent knowledge.',
    status: 'ported',
    icon: Brain,
    kind: 'brain-redirect',
  },
  {
    id: 'brain-blueprints-redirect',
    path: '/brain/blueprints',
    title: 'Blueprints',
    status: 'ported',
    icon: FileStack,
    kind: 'brain-blueprints-redirect',
  },
  {
    id: 'brain-blueprints-type',
    path: '/brain/blueprints/:type',
    title: 'Blueprints',
    subtitle: 'Reusable templates scoped to org or workspace.',
    status: 'ported',
    icon: FileStack,
    kind: 'brain-blueprints-type',
  },
  {
    id: 'brain-knowledge-redirect',
    path: '/brain/knowledge',
    title: 'Knowledge',
    status: 'ported',
    icon: Lightbulb,
    kind: 'brain-knowledge-redirect',
  },
  {
    id: 'brain-knowledge-applies',
    path: '/brain/knowledge/:applies',
    title: 'Knowledge',
    subtitle: 'Natural-language rules that steer agent behavior.',
    status: 'ported',
    icon: Lightbulb,
    kind: 'brain-knowledge-applies',
  },
  // ── YC demo: Plan 05 (Brain Memory) ──────────────────────────────────
  {
    id: 'brain-memory',
    path: '/brain/memory',
    title: 'Memory',
    subtitle: 'Things Lantern has learned about your team.',
    status: 'frame-ready',
    icon: Sparkles,
    kind: 'brain-memory',
  },
  {
    id: 'brain-skills',
    path: '/brain/skills',
    title: 'Skills',
    subtitle: 'Reusable agent procedures.',
    status: 'frame-ready',
    icon: Blocks,
    kind: 'brain-skills',
  },
  // ── /YC demo: Plan 05 ────────────────────────────────────────────────
  // ── YC demo: Brain → Company group ───────────────────────────────────
  {
    id: 'brain-company',
    path: '/brain/company',
    title: 'Company',
    status: 'frame-ready',
    icon: Building2,
    kind: 'brain-company-redirect',
  },
  {
    id: 'brain-company-profile',
    path: '/brain/company/profile',
    title: 'Organization',
    subtitle: 'Mission, stage, funding, and the customers Northwind serves.',
    status: 'frame-ready',
    icon: Building2,
    kind: 'brain-company-profile',
  },
  {
    id: 'brain-company-philosophy',
    path: '/brain/company/philosophy',
    title: 'Hiring philosophy',
    subtitle: 'The bar, calibrations, dealbreakers, and loop principles.',
    status: 'frame-ready',
    icon: ShieldCheck,
    kind: 'brain-company-philosophy',
  },
  {
    id: 'brain-company-headcount',
    path: '/brain/company/headcount',
    title: 'Headcount plan',
    subtitle: '2026 hiring plan by team and quarter.',
    status: 'frame-ready',
    icon: Calendar,
    kind: 'brain-company-headcount',
  },
  {
    id: 'brain-company-employment-history',
    path: '/brain/company/employment-history',
    title: 'Employment history',
    subtitle: 'Past hires, where they came from, and how they ramped.',
    status: 'frame-ready',
    icon: Users,
    kind: 'brain-company-employment-history',
  },
  // ── /YC demo: Brain → Company group ──────────────────────────────────
  // (Plan 04's /v2/reports/cv-screening and /v2/reports/ai-interview routes
  // have been retired — the report is now an overlay rendered inside
  // FullPager, opened via tasksApi/kanban with ?fp=application:<id>&task=<id>.)
  // ── YC demo: Plan 03 (Tasks) ─────────────────────────────────────────
  {
    id: 'tasks',
    path: '/tasks',
    title: 'Tasks',
    tabLabel: 'Tasks',
    subtitle: 'Reviews waiting on you.',
    status: 'frame-ready',
    icon: ListChecks,
    kind: 'tasks',
  },
  // ── /YC demo: Plan 03 ────────────────────────────────────────────────
  {
    id: 'chats-list',
    path: '/v2/chats',
    title: 'Chats',
    tabLabel: 'Chats',
    subtitle: 'Conversations with Lantern.',
    status: 'frame-ready',
    icon: MessageSquare,
    kind: 'chats-list',
  },
  {
    id: 'chat-detail',
    path: '/v2/chats/:chatId',
    title: 'Chat',
    tabLabel: 'Chat',
    subtitle: 'Conversation with Lantern.',
    sourcePath: 'apps/ats-spa/src/app/v2/chats/[id]/page.tsx',
    status: 'frame-ready',
    icon: MessageSquare,
  },
  // ── Searches (renamed from /agent/*; files under app/agent/) ──
  {
    id: 'agent-chats-list',
    path: '/searches',
    title: 'Searches',
    tabLabel: 'Searches',
    subtitle: 'Saved searches and active conversations.',
    status: 'frame-ready',
    icon: Sparkles,
    kind: 'agent-chats-list',
  },
  {
    id: 'agent-new-chat',
    path: '/search/new',
    title: 'New search',
    subtitle: 'Creating a new search.',
    status: 'frame-ready',
    icon: Sparkles,
    kind: 'agent-new-chat',
  },
  {
    id: 'agent-chat-detail',
    path: '/search/:chatId',
    title: 'Search',
    tabLabel: 'Search',
    subtitle: 'Conversation.',
    sourcePath: 'apps/ats-spa/src/app/agent/[id]/page.tsx',
    status: 'frame-ready',
    icon: Sparkles,
  },
  // ── Searches v2 (parallel, isolated experience; agentType search_v2) ──
  {
    id: 'search-v2-list',
    path: '/searches-v2',
    title: 'Searches v2',
    tabLabel: 'Searches v2',
    subtitle: 'A fresh search experience.',
    status: 'frame-ready',
    icon: Sparkles,
    kind: 'search-v2-list',
  },
  {
    id: 'search-v2-new',
    path: '/search-v2/new',
    title: 'New search v2',
    subtitle: 'Creating a new search.',
    status: 'frame-ready',
    icon: Sparkles,
    kind: 'search-v2-new',
  },
  {
    id: 'search-v2-detail',
    path: '/search-v2/:chatId',
    title: 'Search v2',
    tabLabel: 'Search v2',
    subtitle: 'Conversation.',
    sourcePath: 'apps/ats-spa/src/app/agent/[id]/page.tsx',
    status: 'frame-ready',
    icon: Sparkles,
  },
  // ── Dedicated search route (parallel, standalone) ──
  {
    id: 'search-v3-list',
    path: '/searches-v3',
    title: 'Searches',
    tabLabel: 'Searches',
    subtitle: 'Ranked candidate search.',
    status: 'frame-ready',
    icon: Sparkles,
    kind: 'search-v3-list',
  },
  {
    id: 'search-v3-new',
    path: '/search-v3/new',
    title: 'New search',
    subtitle: 'Creating a new search.',
    status: 'frame-ready',
    icon: Sparkles,
    kind: 'search-v3-new',
  },
  {
    id: 'search-v3-detail',
    path: '/search-v3/:chatId',
    title: 'Search',
    tabLabel: 'Search',
    subtitle: 'Conversation.',
    sourcePath: 'apps/ats-spa/src/app/agent/[id]/page.tsx',
    status: 'frame-ready',
    icon: Sparkles,
  },
  {
    id: 'staff-search-v3-autosource',
    path: '/staff/search-v3-autosourcing',
    title: 'Search Auto-source Ops',
    tabLabel: 'Auto-source Ops',
    subtitle: 'Search scheduler and auto-source run monitor.',
    status: 'frame-ready',
    icon: ShieldCheck,
    kind: 'staff-search-v3-autosource',
    keepAlive: false,
  },
  {
    id: 'staff-observability',
    path: '/staff/observability',
    title: 'Observability',
    tabLabel: 'Observability',
    subtitle: 'Enrichment provider health, verification, cache & spend.',
    status: 'frame-ready',
    icon: Activity,
    kind: 'staff-observability',
    keepAlive: false,
  },
  // ── Standalone local talent-pool search (gated by the crypto-local-search org preset) ──
  {
    id: 'talent-pool-search',
    path: '/talent-pool',
    title: 'Talent Pool Search',
    tabLabel: 'Talent Pool',
    subtitle: 'Search your local candidate pool with editable filters.',
    status: 'frame-ready',
    icon: Search,
    kind: 'talent-pool',
  },
  {
    id: 'create-org',
    path: '/v2/create-org',
    title: 'Create Organization',
    subtitle: 'Create a new organization workspace.',
    sourcePath: 'apps/ats/src/app/v2/create-org/page.tsx',
    legacyPath: '/v2/create-org',
    status: 'migration-target',
    icon: Building2,
  },
  {
    id: 'lab-job-cards',
    path: '/lab/job-cards',
    title: 'Job card lab',
    tabLabel: 'Job card lab',
    subtitle: 'Layout variations to choose from.',
    status: 'frame-ready',
    icon: Palette,
    kind: 'lab-job-cards',
  },
  {
    id: 'lab-pipeline',
    path: '/lab/pipeline',
    title: 'Pipeline view lab',
    tabLabel: 'Pipeline view lab',
    subtitle: 'Layout variations for the automation pipeline view.',
    status: 'frame-ready',
    icon: Palette,
    kind: 'lab-pipeline',
  },
  {
    id: 'tmp-kanban',
    path: '/v2/tmp-kanban/:assessmentId',
    title: 'Temporary Kanban',
    tabLabel: 'Kanban',
    subtitle: 'Assessment kanban board.',
    sourcePath: 'apps/ats/src/app/v2/tmp-kanban/[assessmentId]/page.tsx',
    legacyPath: '/v2/tmp-kanban/:assessmentId',
    status: 'migration-target',
    icon: Columns,
  },
];

function buildElement(route: RouteMeta) {
  const v2Element = v2RouteElements[route.id as keyof typeof v2RouteElements];
  if (v2Element) return v2Element;
  if (route.kind === 'home') return <WorkspaceHome />;
  if (route.kind === 'home-v2') return <HomeV2 />;
  if (route.kind === 'home-v3') return <HomeV3 />;
  if (route.kind === 'home-v4') return <HomeV4 />;
  if (route.kind === 'home-v5') return <HomeV5 />;
  if (route.kind === 'home-v3-1') return <HomeV31 />;
  if (route.kind === 'home-v3-2') return <HomeV32 />;
  if (route.kind === 'home-v3-3') return <HomeV33 />;
  if (route.kind === 'home-v3-4') return <HomeV34 />;
  if (route.kind === 'new-tab') return <NewTabPage routes={routeMeta} />;
  if (route.kind === 'permission') return <PermissionMatrixPage route={route} />;
  if (route.kind === 'notetakers') return <NotetakersPage />;
  if (route.kind === 'lab-job-cards') return <JobCardLabPage />;
  if (route.kind === 'lab-pipeline') return <PipelineLabPage />;
  if (route.kind === 'search') return <SearchPage />;
  if (route.kind === 'talent-pool')
    return (
      <TalentPoolGate>
        <TalentPoolSearchPage />
      </TalentPoolGate>
    );

  if (route.kind === 'redirect') {
    const target = REDIRECT_MAP[route.id];
    if (target) return <Navigate to={target} replace />;
  }

  // Settings — fullscreen no-sidebar layout, internal rail
  if (route.kind === 'settings-redirect') return <Navigate to="/settings/account" replace />;
  if (route.kind === 'settings-general') return <SettingsLayout><GeneralPage /></SettingsLayout>;
  if (route.kind === 'settings-account') return <SettingsLayout><AccountPage /></SettingsLayout>;
  if (route.kind === 'settings-organization') return <SettingsLayout><OrganizationPage /></SettingsLayout>;
  if (route.kind === 'settings-job-board') return <SettingsLayout><JobBoardPage /></SettingsLayout>;
  if (route.kind === 'settings-integrations') return <SettingsLayout><IntegrationsPage /></SettingsLayout>;
  if (route.kind === 'settings-ats') return <SettingsLayout><AtsPage /></SettingsLayout>;
  if (route.kind === 'settings-calendar') return <SettingsLayout><CalendarSettingsPage /></SettingsLayout>;
  if (route.kind === 'settings-email') return <SettingsLayout><EmailPage /></SettingsLayout>;
  if (route.kind === 'settings-outreach-accounts')
    return <SettingsLayout><OutreachAccountsPage /></SettingsLayout>;

  // Brain — same fullscreen pattern.
  // The outer rail (BrainLayout / BrainRail) lists every blueprint type and
  // knowledge agent target as a sub-item; the per-type / per-applies pages
  // each render their own scope rail (Org / Workspaces / Personal-for-Knowledge).
  // YC demo: Plan 05 reframes Brain as "Things Jacinta has learned" — default
  // landing redirects there instead of Blueprints.
  if (route.kind === 'brain-redirect') return <Navigate to="/brain/memory" replace />;
  if (route.kind === 'brain-blueprints-redirect') {
    return <Navigate to="/brain/blueprints/email" replace />;
  }
  if (route.kind === 'brain-blueprints-type') {
    return <BrainLayout><BlueprintTypePage /></BrainLayout>;
  }
  if (route.kind === 'brain-knowledge-redirect') {
    return <Navigate to="/brain/knowledge/global" replace />;
  }
  if (route.kind === 'brain-knowledge-applies') {
    return <BrainLayout><KnowledgeAppliesPage /></BrainLayout>;
  }
  if (route.kind === 'brain-memory') {
    return <BrainLayout><JacintaMemoryPage /></BrainLayout>;
  }
  if (route.kind === 'brain-skills') {
    return <BrainLayout><SkillsPage /></BrainLayout>;
  }
  if (route.kind === 'brain-company-redirect') {
    return <Navigate to="/brain/company/profile" replace />;
  }
  if (route.kind === 'brain-company-profile') {
    return <BrainLayout><OrgInfoPage /></BrainLayout>;
  }
  if (route.kind === 'brain-company-philosophy') {
    return <BrainLayout><HiringPhilosophyPage /></BrainLayout>;
  }
  if (route.kind === 'brain-company-headcount') {
    return <BrainLayout><HeadcountPlanPage /></BrainLayout>;
  }
  if (route.kind === 'brain-company-employment-history') {
    return <BrainLayout><EmploymentHistoryPage /></BrainLayout>;
  }

  // YC demo: Plan 04 (Reports) — the dedicated /v2/reports/... routes are
  // gone. The report now renders inside the FullPager overlay (CandidateReport
  // is the canvas); both kanban-card-click and tasks-inbox-click open it via
  // useFullPagerStore.open(...).

  // YC demo: Plan 03 (Tasks) — inbox of reviews assigned to the user.
  if (route.kind === 'tasks') return <TasksPage />;

  if (route.kind === 'sessions-list') return <SessionsListPage />;
  if (route.kind === 'jobs-summary') return <JobsListPage />;
  if (route.kind === 'outreach-inbox') return <OutreachInboxPage />;
  if (route.kind === 'chats-list') return <ChatsListPage />;
  if (route.kind === 'agent-chats-list') return <AgentChatsListPage />;
  if (route.kind === 'agent-new-chat') return <AgentNewChatPage />;
  // search-v2 reuses the v1 agent chat pages; surface.ts makes them
  // route-aware (agentType + links). The detail page loads lazily via
  // v2RouteElements['search-v2-detail'].
  if (route.kind === 'search-v2-list') return <AgentChatsListPage />;
  if (route.kind === 'search-v2-new') return <AgentNewChatPage />;
  // search-v3 has a real ops table; new/detail still use the agent surface.
  if (route.kind === 'search-v3-list') return <SearchV3ListPage />;
  if (route.kind === 'search-v3-new') return <AgentNewChatPage />;
  if (route.kind === 'staff-search-v3-autosource') return <SearchV3AutoSourceOpsPage />;
  if (route.kind === 'staff-observability') return <ObservabilityPage />;

  return <MigrationPage route={route} />;
}

function RoutePermissionGate({
  required,
  children,
}: {
  required: string[];
  children: ReactNode;
}) {
  const { hasAnyPermission, ready } = useAuth();
  if (!ready) return null;
  if (!hasAnyPermission(...required)) return <Navigate to="/v2/jobs" replace />;
  return <>{children}</>;
}

/** Gate the Talent Pool route behind the `crypto-local-search` org preset (defense in depth vs the nav). */
function TalentPoolGate({ children }: { children: ReactNode }) {
  const { organization, ready } = useAuth();
  if (!ready) return null;
  if (!hasCryptoLocalSearch(organization)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export const appRoutes: AppRoute[] = routeMeta.map((route) => {
  const element = buildElement(route);
  if (route.requireAnyPermission && route.requireAnyPermission.length > 0) {
    return {
      ...route,
      element: (
        <RoutePermissionGate required={route.requireAnyPermission}>{element}</RoutePermissionGate>
      ),
    };
  }
  return { ...route, element };
});

export function pathToRegex(path: string) {
  const pattern = path
    .replace(/\/+$/, '')
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/:([A-Za-z0-9_]+)/g, '[^/]+');

  return new RegExp(`^${pattern || '/'}$`);
}

export function matchRoute(pathname: string) {
  const normalized = pathname.replace(/\/+$/, '') || '/';

  return appRoutes
    .slice()
    .sort((a, b) => b.path.length - a.path.length)
    .find((route) => pathToRegex(route.path).test(normalized));
}

export function interpolateLegacyPath(route: AppRoute, pathname: string) {
  if (!route.legacyPath || !route.path.includes(':')) return route.legacyPath || pathname;

  const routeParts = route.path.split('/');
  const pathParts = pathname.split('/');

  return routeParts.reduce((acc, part, index) => {
    if (!part.startsWith(':')) return acc;
    return acc.replace(part, pathParts[index] || '');
  }, route.legacyPath);
}

export function getRouteById(routeId: string) {
  return appRoutes.find((route) => route.id === routeId);
}
